import { Routes } from '@angular/router';
import { authGuard, guestGuard } from './core/guards/auth.guard';
import { assignedEndpointGuard } from './core/guards/assigned-endpoint.guard';
import { configureDocumentGuard } from './core/guards/configure-document.guard';
import { ShellComponent } from './layout/shell/shell';

export const routes: Routes = [
  // ── Auth pages (no shell/sidebar) ─────────────────────────────────────────
  {
    path: 'auth',
    canActivate: [guestGuard],
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login').then((m) => m.LoginComponent),
        title: 'Sign in – IDP Admin',
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./features/auth/register/register').then((m) => m.RegisterComponent),
        title: 'Sign up – IDP Admin',
      },
    ],
  },

  // ── Main ERP application (inside shell with sidebar + tab-bar) ────────────
  {
    path: '',
    component: ShellComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'api-key-integration', pathMatch: 'full' },

      // Dashboard
      {
        path: 'erp-dashboard',
        loadComponent: () =>
          import('./features/erp-dashboard/erp-dashboard').then((m) => m.ErpDashboardComponent),
        title: 'ERP Dashboard',
      },
      {
        path: 'api-key-integration',
        loadComponent: () =>
          import('./features/key-modules/key-modules').then((m) => m.KeyModulesComponent),
        title: 'Manage API Key',
      },
      { path: 'key-modules', redirectTo: 'api-key-integration', pathMatch: 'full' },
      {
        path: 'usage',
        loadComponent: () => import('./features/usage/usage').then((m) => m.UsageComponent),
        title: 'Usage',
      },
      {
        path: 'configure-document',
        canActivate: [configureDocumentGuard],
        loadComponent: () =>
          import('./features/configure-document').then((m) => m.ConfigureDocumentComponent),
        title: 'Configure Document',
      },
      {
        path: 'playground',
        loadComponent: () => import('./features/playground').then((m) => m.PlaygroundComponent),
        title: 'Playground',
      },
      {
        path: 'integration-v1',
        loadComponent: () =>
          import('./features/integration/integration').then((m) => m.IntegrationComponent),
        title: 'Integration v1',
      },
      {
        path: 'integration',
        loadComponent: () =>
          import('./features/integration-v2/integration-v2-shell').then(
            (m) => m.IntegrationV2ShellComponent,
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/integration-v2/catalog/integration-v2-catalog').then(
                (m) => m.IntegrationV2CatalogComponent,
              ),
            title: 'Integration',
          },
          {
            path: 'documentation',
            canActivate: [assignedEndpointGuard],
            loadComponent: () =>
              import('./features/integration-v2/guide/integration-v2-guide').then(
                (m) => m.IntegrationV2GuideComponent,
              ),
            title: 'Integration — Documentation',
          },
          {
            path: 'playground',
            canActivate: [assignedEndpointGuard],
            loadComponent: () =>
              import('./features/integration-v2/playground/integration-v2-playground').then(
                (m) => m.IntegrationV2PlaygroundComponent,
              ),
            title: 'Integration — Playground',
          },
        ],
      },
      { path: 'integration-v2', redirectTo: 'integration', pathMatch: 'full' },
      {
        path: 'integration-v2/documentation',
        redirectTo: 'integration/documentation',
        pathMatch: 'full',
      },
      {
        path: 'integration-v2/playground',
        redirectTo: 'integration/playground',
        pathMatch: 'full',
      },
      {
        path: 'webhooks',
        loadComponent: () =>
          import('./features/webhooks/webhooks-shell').then((m) => m.WebhooksShellComponent),
        children: [
          { path: '', redirectTo: 'endpoints', pathMatch: 'full' },
          {
            path: 'endpoints',
            loadComponent: () =>
              import('./features/webhooks/endpoints/webhooks-endpoints').then(
                (m) => m.WebhooksEndpointsComponent,
              ),
            title: 'Webhooks — Endpoints',
          },
          {
            path: 'activity',
            loadComponent: () =>
              import('./features/webhooks/activity/webhooks-activity').then(
                (m) => m.WebhooksActivityComponent,
              ),
            title: 'Webhooks — Activity',
          },
          {
            path: 'activity/:id',
            loadComponent: () =>
              import('./features/webhooks/activity-detail/webhooks-activity-detail').then(
                (m) => m.WebhooksActivityDetailComponent,
              ),
            title: 'Webhooks — Activity detail',
          },
        ],
      },

      // Utility pages (kept from base theme)
      {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile').then((m) => m.ProfileComponent),
        title: 'My Profile',
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/settings').then((m) => m.SettingsComponent),
        title: 'Settings',
      },
    ],
  },

  { path: '**', redirectTo: 'erp-dashboard' },
];
